import { match, P } from "ts-pattern";
import { blockElements, markElements, validElements } from "../utils/constants.js";
export const isOrderedListBlock = (node) => node.tagName === "ol";
export const isUnorderedListBlock = (node) => node.tagName === "ul";
export const isListBlock = (node) => isUnorderedListBlock(node) || isOrderedListBlock(node);
export const isListItem = (node) => node.tagName === "li";
// any link besides item link is considered external for portable text transformation purposes
export const isExternalLink = (node) => isAnchor(node) && getItemLinkReferenceData(node.attributes) === null;
export const isAnchor = (node) => node.tagName === "a";
export const isTableCell = (node) => node.tagName === "td";
export const isLineBreak = (node) => node.tagName === "br";
export const isBlockElement = (node) => blockElements.includes(node.tagName);
export const isValidElement = (node) => validElements.includes(node.tagName);
export const isMarkElement = (node) => markElements.includes(node.tagName);
/**
 * Returns `true` for text nodes and type guards the node as `DomTextNode`.
 */
export const isText = (node) => node.type === "text";
/**
 * Returns `true` for HTML nodes and type guards the node as `DomHtmlNode`.
 */
export const isElement = (node) => node.type === "tag";
/**
 * Returns `true` if the node is a linked item node (`<object></object>`) and narrows type guard.
 */
export const isLinkedItemOrComponent = (node) => match(node)
    .with({
    type: "tag",
    tagName: "object",
    attributes: P.when(attrs => attrs["type"] === "application/kenticocloud"),
}, () => true)
    .otherwise(() => false);
/**
 * Returns `true` if the node is a rich text image node (`<figure></figure>`) and narrows type guard.
 */
export const isImage = (node) => match(node)
    .with({
    type: "tag",
    tagName: "figure",
    attributes: P.when(attrs => typeof attrs["data-asset-id"] === "string"
        || typeof attrs["data-asset-external-id"] === "string"
        || typeof attrs["data-asset-codename"] === "string"),
}, () => true)
    .otherwise(() => false);
/**
 * Returns `true` if the node is a link to a content item and narrows type guard.
 */
export const isItemLink = (node) => match(node)
    .with({
    type: "tag",
    tagName: "a",
    attributes: P.when(attrs => typeof attrs["data-item-id"] === "string"
        || typeof attrs["data-item-external-id"] === "string"
        || typeof attrs["data-item-codename"] === "string"),
}, () => true)
    .otherwise(() => false);
export const throwError = (msg) => {
    throw new Error(msg);
};
export const isAssetLink = (node) => match(node)
    .with({
    type: "tag",
    tagName: "a",
    attributes: P.when(attrs => typeof attrs["data-asset-id"] === "string"
        || typeof attrs["data-asset-external-id"] === "string"
        || typeof attrs["data-asset-codename"] === "string"),
}, () => true)
    .otherwise(() => false);
const createReferenceDataGetter = (refAttributeTypes) => (attributes) => {
    const refInfo = refAttributeTypes.find(({ attr }) => attributes[attr]);
    return refInfo
        ? { reference: attributes[refInfo.attr], refType: refInfo.refType }
        : null;
};
const assetReferences = [
    { attr: "data-asset-id", refType: "id" },
    { attr: "data-asset-external-id", refType: "external-id" },
    { attr: "data-asset-codename", refType: "codename" },
];
const itemOrComponentReferences = [
    { attr: "data-id", refType: "id" },
    { attr: "data-external-id", refType: "external-id" },
    { attr: "data-codename", refType: "codename" },
];
const itemLinkReferences = [
    { attr: "data-item-id", refType: "id" },
    { attr: "data-item-external-id", refType: "external-id" },
    { attr: "data-item-codename", refType: "codename" },
];
export const getAssetReferenceData = createReferenceDataGetter(assetReferences);
export const getItemOrComponentReferenceData = createReferenceDataGetter(itemOrComponentReferences);
export const getItemLinkReferenceData = createReferenceDataGetter(itemLinkReferences);
export const getAllNewLineAndWhiteSpace = /\n\s*/g;
